import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  IndexTable,
  InlineGrid,
  InlineStack,
  Modal,
  Pagination,
  Select,
  Text,
  Tooltip,
} from '@shopify/polaris';
import { ViewIcon } from '@shopify/polaris-icons';
import apiClient from '../utils/api';
import { DEFAULT_PAGE_SIZE, PageSizeSelect } from '../utils/table';
import {
  SOURCE_COLUMN_TIP, runsFrom, sourceLabel, sourceTip, statusTip,
} from '../utils/runs';

/** How often the table re-reads the queue. It is a queue monitor; stale is its failure mode. */
const POLL_MS = 15000;

/** One shared empty array, so an unloaded table does not hand the sort memo a new one to sort. */
const EMPTY_JOBS = [];

/** Same reason, for the state tallies the summary line is built from. */
const EMPTY_COUNTS = {};

/**
 * The connector's background work, one row per job.
 *
 * WHAT THIS REPLACES. Three counts and a click-through to the failures. "2 failed" was not
 * answerable on the screen that said it, and — worse — a queue sitting at all zeros looked
 * exactly like a connector that had been switched off, because a job stopped by a Sync
 * Settings toggle COMPLETES rather than failing and writes no Sync History row. It appeared
 * in no count at all.
 *
 * ONE component, two placements: the Retailer Accounts page shows every job, a brand's Retail
 * Connect tab shows its own — including the RETAILER-level jobs for the accounts it is
 * connected to, because order ingest, offer reads and inventory pushes are queued per account
 * and split across brands inside the handler. A brand-only filter would leave that tab almost
 * always empty while "the offer read for Macy's is failing" sat outside it.
 */

/**
 * Plain language for the job types, matched to the wording in Sync Settings so the same
 * operation is not called two different things on two screens. The raw type stays available
 * on hover, because it is what appears in the logs.
 */
const JOB_LABELS = {
  'account-order-sync': 'Fetch orders',
  'order-sync': 'Create Shopify orders',
  'tracking-push': 'Push tracking',
  'inventory-sync': 'Queue a stock change',
  'inventory-flush': 'Push inventory',
  'sku-map-refresh': 'Fetch SKUs',
  'offer-import-poll': 'Check push results',
  'catalogue-sync': 'Resync Shopify catalogue',
  'location-sync': 'Resync locations/warehouses',
  // Retired: the nightly verification was removed. Kept so a job still sitting in Redis from
  // before that change renders with a name instead of a raw type string.
  'marketplace-verify': 'Verify credentials (retired)',
  'ledger-prune': 'Prune sync history',
};

/**
 * Connector-queue jobs that read the brand's OWN Shopify store rather than a retailer.
 *
 * They share a queue with the retailer work because that is where the Shopify client, the
 * session and the read toggle live — but sharing a queue is not the same as being the same
 * kind of work, and the Source column exists to tell someone which team to call.
 *
 * KEPT IN STEP BY HAND with SHOPIFY_READ_MIRAKL_JOB_TYPES in the backend's
 * brand-runs-service.js, which makes the same judgement for Sync History. The two cannot
 * share a list: these are BullMQ job types (lowercase, 'location-sync') and those are
 * MiraklSyncJobType enum values ('LOCATION_SYNC'). Add a third Shopify read to one and not
 * the other and the two screens disagree about what Source means.
 */
const SHOPIFY_READ_TYPES = new Set(['catalogue-sync', 'location-sync']);

/**
 * Billing's run statuses, mapped onto the queue states this table already sorts and filters by.
 *
 * Billing does not use the connector's queue, so nothing here is a rename of the same value —
 * it is a claim that the two systems mean the same thing by a word, and each one is deliberate:
 * PENDING is work that has not started (waiting), RUNNING is work in flight (active), and
 * COMPLETED and FAILED mean what they say.
 *
 * RETRYING and CANCELLED have no connector equivalent and are NOT folded into a neighbour.
 * Calling a retry "Failed" would say it has stopped, and calling a cancellation "Skipped" would
 * say a toggle stopped it — both read as a different event to anyone deciding whether to act.
 * They get their own badges, their own place in the severity order, and their own filter
 * options, which only appear where a billing row can appear.
 */
const BILLING_STATE = {
  COMPLETED: 'completed',
  FAILED: 'failed',
  RUNNING: 'active',
  PENDING: 'waiting',
  RETRYING: 'retrying',
  CANCELLED: 'cancelled',
};

/**
 * States and job types the connector route has never heard of.
 *
 * Selecting one must not be sent to `getQueueJobs` as a filter: it is an enum that route does
 * not define, and an unknown filter is answered by guesswork rather than by an error. So the
 * value is stripped from the connector request and the connector rows are dropped here
 * instead, which is the same result stated honestly — no connector job can be "Retrying".
 */
const BILLING_ONLY_STATES = new Set(['retrying', 'cancelled']);

const isBillingOnlyType = (type) => Boolean(type) && !(type in JOB_LABELS);

/**
 * What each job actually DOES, for the detail dialog.
 *
 * The dialog used to print the raw type — "sku-map-refresh" — and the queue's internal id.
 * Neither appears on any other screen, so there was nothing to compare them against and
 * nothing an admin could do with either, while the two questions they actually have went
 * unanswered: what is it doing to my data, and what happens next.
 *
 * Present tense and concrete, naming which side it writes to. An admin deciding whether a
 * stuck job matters needs to know it writes to a retailer, not that it is called a flush.
 */
const JOB_EXPLAIN = {
  'account-order-sync': 'Collects new orders from the retailer and stores them in 2W. It reads only — nothing is sent to the retailer, and no Shopify order is created by this job.',
  'order-sync': "Creates the Shopify order for retailer orders that have been matched to a brand. This writes to the brand's Shopify store.",
  'tracking-push': 'Tells the retailer an order has shipped, with its tracking number. This writes to the retailer.',
  'inventory-sync': 'Works out the new stock figure after a change in Shopify and puts it in the queue to be sent. Nothing reaches a retailer from this job.',
  'inventory-flush': 'Sends the queued stock figures to the retailer. This is the job that actually changes stock at their end.',
  'sku-map-refresh': 'Reads everything the retailer lists, so 2W knows which of their SKUs belong to which brand. It reads only.',
  'offer-import-poll': 'Asks the retailer what happened to stock we already sent — applied, or refused with a reason.',
  'catalogue-sync': "Reads the brand's Shopify catalogue into 2W, so SKUs and stock can be compared against what the retailer holds. It reads only.",
  'marketplace-verify': 'A retired check that is no longer part of 2W. A job of this type still in the queue is left over from before it was removed, and it cannot succeed.',
  'ledger-prune': 'Housekeeping: removes old run records so this history stays readable. It touches no retailer and no brand.',
};

/** What happens next, which is the question a queued or failed job actually raises. */
const NEXT_STEP = {
  waiting: 'It is queued and will start on its own.',
  active: 'It is running now. Nothing to do.',
  delayed: 'It is scheduled and will start at the time shown.',
  completed: 'It has finished. Nothing further will happen.',
  failed: 'It stopped. If it has attempts left it will be retried on its own; if not, the cause needs fixing and a fresh run.',
  skipped: 'It was not run, and it will be skipped again on its next turn until the reason below is dealt with.',
  retrying: 'It failed and is being tried again on its own.',
};

const STATE_BADGE = {
  failed: { tone: 'critical', label: 'Failed' },
  // A job that COMPLETED while reporting its own failure — the order ingest does this
  // deliberately when OR11 throws partway through paging, so a retry does not immediately
  // re-page. Right for the queue, and it used to render as a green "Done" with no reason.
  errored: { tone: 'critical', label: 'Failed (recorded)' },
  active: { tone: 'info', label: 'Running' },
  waiting: { tone: 'attention', label: 'Waiting' },
  delayed: { tone: 'attention', label: 'Scheduled' },
  skipped: { tone: 'warning', label: 'Skipped' },
  // Billing only. A retry is still live work, so it is not green and not the flat "Failed"
  // that would say it had stopped; a cancellation is neither a failure nor a toggle skip.
  retrying: { tone: 'warning', label: 'Retrying' },
  cancelled: { tone: 'new', label: 'Cancelled' },
  completed: { tone: 'success', label: 'Done' },
};

const STATE_OPTIONS = [
  { label: 'All states', value: '' },
  { label: 'Failed', value: 'failed' },
  { label: 'Failed (recorded)', value: 'errored' },
  { label: 'Blocked', value: 'blocked' },
  { label: 'Running', value: 'active' },
  { label: 'Waiting', value: 'waiting' },
  { label: 'Scheduled', value: 'delayed' },
  { label: 'Skipped', value: 'skipped' },
  { label: 'Done', value: 'completed' },
];

/**
 * Billing's two extra states, offered in BOTH placements.
 *
 * They used to be brand-only, on the rule that a filter which can never match is worse than a
 * missing one — it answers "No jobs match these filters" and leaves the reader thinking they
 * looked. That rule has not changed; what changed is the fact under it. The global page now
 * reads the merged route and shows billing's outstanding work beside the connector's, so a
 * billing row can appear on either screen and Retrying is a state either can hold.
 */
export const stateOptions = () => [
  ...STATE_OPTIONS,
  { label: 'Retrying', value: 'retrying' },
  { label: 'Cancelled', value: 'cancelled' },
];

/**
 * The sort order for the State column, worst first.
 *
 * NOT alphabetical. Alphabetically, "Done" leads and "Waiting" trails, which on a queue monitor
 * buries the only rows anyone opens this table to find. Read top-down this is severity: work
 * that is stuck and needs a person, then the two kinds of failure ADJACENT to each other (a
 * recorded failure is the same event as a failure — it just completed while reporting it), then
 * live work, then work that has not run yet, and routine outcomes last.
 *
 * `blocked` is not a queue state: it is `skipPermanent`, the same value the badge renders from,
 * so the sort key is computed off the badge rather than off `job.state`.
 */
const STATE_RANK = [
  'blocked',    // Blocked — deactivated account, missing key, stale scheduler: re-fires forever
  'failed',     // Failed
  'errored',    // Failed (recorded)
  'retrying',   // Retrying — billing only: it has already failed once, and is still live
  'active',     // Running
  'waiting',    // Waiting
  'delayed',    // Scheduled
  'skipped',    // Skipped — not a failure, but worth seeing above the routine rows
  'cancelled',  // Cancelled — billing only: stopped on purpose, so it ranks with Skipped
  'completed',  // Done
];

/**
 * Per-column wording for the sort-direction hover.
 *
 * Polaris DROPS a sortable heading's own `tooltipContent` unless `sortToggleLabels` is supplied,
 * and it reads `sortToggleLabels[index][direction]` UNGUARDED — so a map missing one sortable
 * index is a TypeError at render, not a missing tooltip. Every column in this table carries a
 * tooltip that has to survive, so this is keyed by column KEY and expanded to a complete
 * index map below: adding, removing or reordering a column cannot leave a hole.
 */
const SORT_TOGGLE_LABELS = {
  // A-Z rather than naming the ends. With three sources the old "Connector first / Billing
  // first" named two of them and silently left out the third, which the sort does include.
  source: { ascending: 'A-Z', descending: 'Z-A' },
  state: { ascending: 'Worst first', descending: 'Routine first' },
  job: { ascending: 'A-Z', descending: 'Z-A' },
  who: { ascending: 'A-Z', descending: 'Z-A' },
  attempts: { ascending: 'Fewest first', descending: 'Most first' },
  when: { ascending: 'Oldest first', descending: 'Newest first' },
};

/** The fallback keeps the index map total even for a column that is not sortable. */
const SORT_TOGGLE_FALLBACK = { ascending: 'Ascending', descending: 'Descending' };

/**
 * Two of these can never match inside a brand's tab, so that view is offered a shorter list.
 *
 * The brand filter keeps a job when it names the brand or names one of the brand's retailer
 * accounts. `marketplace-verify` carries NEITHER id by design, and `offer-import-poll` is
 * scheduled globally with a null marketplaceId — so selecting "Verify credentials" or "Check
 * push results" on a brand tab always answered "No jobs match these filters".
 *
 * The second one matters: that poller is what decides whether an inventory push actually
 * landed, which is the story the SKU table's "Attempted, not confirmed" badge tells. Offering
 * a filter for it on a screen that can never show it is worse than not offering it.
 */
const GLOBAL_ONLY_TYPES = new Set(['marketplace-verify', 'ledger-prune', 'offer-import-poll']);

const typeOptions = (brandScoped, billingTypes = []) => [
  { label: 'All jobs', value: '' },
  ...Object.entries(JOB_LABELS)
    .filter(([value]) => !(brandScoped && GLOBAL_ONLY_TYPES.has(value)))
    .map(([value, label]) => ({ label, value })),
  // Billing's types are not a fixed list here — they are read off the rows that came back, so
  // a job type billing adds is filterable the day it first runs rather than the day someone
  // remembers to add it above. Without this the billing rows were the only ones in the table
  // that no Job filter could select.
  ...billingTypes,
];

/**
 * The retailer and brand a row names, or null.
 *
 * Both are read by the CELL and by the sort key, because they have to agree: this table's own
 * rule is that a sort reads the value its column renders. The `|| name` arm is what lets a
 * billing row participate — the merged run shape carries `retailerName` and `brandName` but no
 * ids, so a check on the id alone silently blanked the whole column for half the rows.
 */
const retailerOf = (job) => ((job.marketplaceId || job.marketplaceName)
  ? (job.marketplaceName || 'Deleted retailer account')
  : null);

const brandOf = (job) => ((job.brandId || job.brandName)
  ? (job.brandName || 'Deleted brand')
  : null);

/** The millisecond stamp the When column reads: finished, else started, else queued. */
const runWhen = (run) => {
  const iso = run.completedAt || run.startedAt || run.createdAt;
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

/**
 * One merged run -> one row of this table.
 *
 * Mapped into the queue's own job shape rather than rendered by a second code path, so every
 * filter, every sort key, the pager and the detail modal keep working on it without knowing it
 * came from somewhere else. The original run is kept on `run` for the hovers, which need the
 * fields the queue shape has no room for — the attempt limit, the note, the exact status.
 */
const billingJobFrom = (run) => ({
  source: 'billing',
  jobId: run.id,
  type: run.type || null,
  // The server's plain English, so a billing type renders as a name rather than an enum. The
  // raw type stays on the hover for whoever greps the logs next.
  label: run.label || String(run.type || '').replace(/_/g, ' '),
  // An unrecognised status keeps its own value rather than being forced into a queue state:
  // the badge falls back to printing it, which is how anyone finds out billing added one.
  state: BILLING_STATE[run.status] || String(run.status || '').toLowerCase(),
  skipPermanent: false,
  manual: false,
  marketplaceId: null,
  marketplaceName: run.retailerName || null,
  // The merged route carries the id as well as the name now. Matched on the ID by the brand
  // filter: two brands can be spelled alike, and the name is also the one field that goes
  // null when a brand row is deleted out from under its history.
  brandId: run.brandId || null,
  brandName: run.brandName || null,
  attemptsMade: run.attempts ?? null,
  attempts: run.maxAttempts ?? null,
  failedReason: run.error || null,
  skipReason: null,
  // Surfaced as the Reason when there is no error, rather than dropped. A run that recorded
  // why it did nothing is not the same row as one that recorded nothing at all.
  note: run.note || null,
  when: runWhen(run),
  timestamp: run.createdAt ? new Date(run.createdAt).getTime() : null,
  run,
});

/**
 * Date AND time, always — "Sep 1, 05:48 PM".
 *
 * This used to degrade to a bare relative age and then, past 24 hours, to a bare date: every
 * row older than a day read "Sep 1" with no time at all. On a table whose job is "when did
 * this run", a date on its own cannot distinguish five jobs from the same day, which is
 * exactly what a repeating scheduler produces. Same format as the SKU table's As-of column, so
 * two timestamps on one screen are comparable.
 */
const when = (ms) => {
  if (!ms) return '—';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}, `
    + `${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
};

/** The relative age, kept for the hover — useful on a live queue, too noisy as the column. */
const relative = (ms) => {
  if (!ms) return null;
  const secs = Math.round((Date.now() - ms) / 1000);
  if (secs < 0) return `in ${Math.abs(secs) < 60 ? `${Math.abs(secs)}s` : `${Math.round(Math.abs(secs) / 60)}m`}`;
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
};

const truncate = (text, max = 70) => {
  const s = String(text || '');
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

/**
 * A row with nothing in the sorted column sorts to the END in BOTH directions.
 *
 * Every one of these columns can be empty — a fanned-out job names no retailer, a job that has
 * not run has no attempt count, a queued job may have no timestamp — and all three render as
 * "—" or as "All retailers". Sorted as a small value they would take the top of the ascending
 * page, so the answer to "oldest job" would be a row with no date at all. Kept at the bottom,
 * a blank always reads as a blank. Note `0` and `''` are NOT the same thing here: an attempt
 * count of zero is a real, displayed value.
 */
const isBlank = (v) => v === null || v === undefined || v === '' || Number.isNaN(v);

/**
 * Deterministic ordering inside a tie, so a sorted page does not reshuffle under the 15-second
 * poll: newest first, then the job id. Not flipped by the sort direction — it is a tiebreak,
 * not a second sort the reader asked for.
 */
const tiebreak = (a, b) => (b.when || 0) - (a.when || 0)
  || String(a.jobId || '').localeCompare(String(b.jobId || ''));

const compareValues = (a, b) => {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  // `numeric` so a name ending in a number sorts 2 before 10 the way the eye expects.
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

export default function QueueTable({
  brandId = null,
  marketplaceOptions = [],
  // Only read UNSCOPED, where the table spans every brand and one of them is a question worth
  // asking. On a brand's tab every row already belongs to that brand, so the filter is not
  // offered rather than offered with one option.
  brandOptions = [],
  refreshToken = 0,
}) {
  const brandScoped = Boolean(brandId);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Billing's half of the queue, held apart from `data` because the two come from two routes
  // that fail independently: one being down must not empty the rows the other returned.
  const [billingRuns, setBillingRuns] = useState(EMPTY_JOBS);
  const [billingError, setBillingError] = useState(null);
  // The value is no longer read — the counts strip that showed it is gone — but the
  // setter still runs on every load, so the state stays rather than leaving a dangling
  // call. Renamed to say it is deliberately unused.
  const [, setUpdatedAt] = useState(null);
  const [state, setState] = useState('');
  const [type, setType] = useState('');
  const [marketplaceId, setMarketplaceId] = useState('');
  // The GLOBAL page's brand narrowing. Sent to the connector route as its `brandId`, which is
  // the same filter a brand's own tab uses — so picking a brand here answers exactly what that
  // tab answers, retailer-account work included, rather than a second, narrower definition of
  // "this brand's jobs".
  const [brandFilter, setBrandFilter] = useState('');
  const [openJob, setOpenJob] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  /**
   * Opens newest-first, with the arrow on Date & Time.
   *
   * The API orders its answer by STATE — failures, then running, then queued, then finished —
   * and only sorts by time inside each of those blocks. Nothing on screen says that grouping
   * is there, so the Date & Time column read as a shuffle: a row from an hour ago above one
   * from a minute ago, for a reason the table never gave. Date is the order a queue is read
   * in, so it is the order it opens in, and now the heading says which column did it.
   *
   * Held as a column KEY, not an index. The columns are not identical in both placements —
   * the brand filter and the Source column have each been one-sided at some point — and a
   * hardcoded index would silently open a placement on whichever column happened to land
   * there.
   */
  const [sortColumnKey, setSortColumnKey] = useState('when');
  const [sortDirection, setSortDirection] = useState('descending');

  // Read by the poll so a filter change does not need the interval rebuilt, and so the poll
  // always sends the filters currently on screen.
  const filters = useRef({ state, type, marketplaceId, brandFilter });
  filters.current = { state, type, marketplaceId, brandFilter };

  // Only the NEWEST request may write state. The 15-second poll and a filter change can be in
  // flight together, and `filters.current` is read at call time — so an in-flight poll carries
  // the OLD filters and, resolving last, put unfiltered rows on screen under a set Select.
  const requestSeq = useRef(0);

  const load = useCallback(async (quiet = false) => {
    const seq = ++requestSeq.current;
    if (!quiet) setLoading(true);
    const { state: st, type: ty, marketplaceId: mp, brandFilter: bf } = filters.current;
    // The prop wins: a brand's own tab is not narrowable to another brand.
    const scopeBrandId = brandId || bf || '';

    // SETTLED separately, not Promise.all'd. A brand's queue now has two halves, and all-or-
    // nothing would let a billing outage blank the connector rows that were fetched fine —
    // on the one screen whose job is to say what is running.
    const [queue, billing] = await Promise.allSettled([
      apiClient.getQueueJobs({
        // A value the connector route does not define is not sent to it as a filter; the rows
        // it could never match are dropped below instead. See BILLING_ONLY_STATES.
        ...(BILLING_ONLY_STATES.has(st) ? {} : { state: st }),
        ...(isBillingOnlyType(ty) ? {} : { type: ty }),
        marketplaceId: mp,
        ...(scopeBrandId ? { brandId: scopeBrandId } : {}),
      }),
      // BILLING IS READ ON BOTH PAGES NOW.
      //
      // The global page used to skip this half entirely, so a section called Background Jobs
      // showed no billing work at all — the one place an admin looks to ask "is anything
      // stuck?" quietly omitted a whole system. `getRuns` is the same merged route without a
      // brand, which is a different question rather than a missing argument.
      //
      // Asked for UNFILTERED either way: the merged route takes a scope and a limit, not a
      // state or a job type, so the filters on screen are applied to these rows below.
      brandId
        ? apiClient.getBrandRuns(brandId, { scope: 'queue' })
        : apiClient.getRuns({ scope: 'queue' }),
    ]);
    if (seq !== requestSeq.current) return;

    if (queue.status === 'fulfilled') {
      setData(queue.value);
      setUpdatedAt(Date.now());
      setError(null);
    } else {
      setError(queue.reason?.message || 'Failed to read the queue');
    }

    if (billing.status === 'fulfilled') {
      setBillingRuns(runsFrom(billing.value).map(billingJobFrom));
      setBillingError(null);
    } else {
      // Named as the half it is, with the underlying message kept. A bare "could not read the
      // queue" over a table still full of connector rows would be read as a warning about
      // those rows, and a bare server message says nothing about which half is missing.
      const why = billing.reason?.message ? `: ${billing.reason.message}` : '';
      setBillingError(`Could not read the billing half of this queue${why}. `
        + 'The rows below are connector work only.');
    }

    if (!quiet) setLoading(false);
  }, [brandId]);

  useEffect(() => {
    load();
  }, [load, refreshToken, state, type, marketplaceId, brandFilter]);

  // A filter change, a new row count, or a NEW SORT starts again at page 1. Without this,
  // narrowing a filter while on page 6 shows an empty table and reads as "no jobs match" — and
  // it is the filter Selects that sit directly above the rows, so the mismatch looks like their
  // fault. Sorting belongs in the same reset: the point of sorting worst-first is to put the
  // failures on the first page, and staying on page 6 lands in the middle of the new order.
  //
  // Nothing is persisted: the size is component state, so both placements of this table keep
  // their own, and a reload starts at the default.
  useEffect(() => {
    setPage(1);
  }, [state, type, marketplaceId, brandFilter, pageSize, sortColumnKey, sortDirection]);

  // Quiet refresh: no spinner, so a table someone is reading does not flash every 15s.
  useEffect(() => {
    const id = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  /**
   * Jobs that run on the connector's queue but read the brand's OWN Shopify store.
   *
   * Tagged here rather than server side because `source` has never been a field the queue
   * route sets — billing rows get it when they are mapped, and a connector row simply has
   * none, which `sourceLabel` prints as "Connector". That fallback was answering "who do I
   * call when this fails" with the wrong team for these two: neither talks to a retailer,
   * and the location sync exists only to feed a billing setting.
   */
  const connectorJobs = useMemo(() => {
    const raw = data?.jobs || EMPTY_JOBS;
    // Identity preserved when nothing matches, so the memos downstream that depend on this
    // array do not re-run on every poll of a queue holding no Shopify reads.
    if (!raw.some((j) => SHOPIFY_READ_TYPES.has(j.type))) return raw;
    return raw.map((j) => (SHOPIFY_READ_TYPES.has(j.type) ? { ...j, source: 'shopify' } : j));
  }, [data]);
  // Memoised, not `|| {}` inline: `summaryCounts` below depends on it, and a fresh literal on
  // every render of a queue that has not loaded yet re-runs that tally for nothing.
  const stateCounts = useMemo(() => data?.stateCounts || EMPTY_COUNTS, [data]);
  const filtered = Boolean(state || type || marketplaceId || brandFilter);

  // The retailer filter is an id; a billing row carries the retailer's NAME and no id, because
  // the merged shape has no field for one. Resolving the id through the Select's own option
  // list matches on the exact string already on screen rather than guessing.
  const retailerFilterName = useMemo(
    () => marketplaceOptions.find((o) => o.value === marketplaceId)?.label || null,
    [marketplaceOptions, marketplaceId],
  );

  /**
   * The filters, applied to billing's rows here rather than by the route that returned them.
   *
   * Each test reads the SAME field the corresponding connector filter is applied to server
   * side, so the three Selects mean one thing across both halves of the list. A retailer
   * filter drops every billing row that names no retailer — which is correct: those rows are
   * not work on that account, and keeping them would make the filter look broken.
   */
  const billingVisible = useMemo(() => billingRuns.filter((job) => {
    if (state && job.state !== state) return false;
    if (type && job.type !== type) return false;
    if (marketplaceId && job.marketplaceName !== retailerFilterName) return false;
    // The connector half is narrowed by the route; this half is narrowed here, on the id the
    // merged route carries. A billing run belongs to exactly one brand — its table requires
    // the column — so there is no account-level case to fold in as there is on the other side.
    if (brandFilter && job.brandId !== brandFilter) return false;
    return true;
  }), [billingRuns, state, type, marketplaceId, brandFilter, retailerFilterName]);

  // No connector job can be "Retrying", or carry a job type billing defines. Selecting one
  // drops them all, which is the same answer the server would give if it knew the value.
  const connectorExcluded = BILLING_ONLY_STATES.has(state) || isBillingOnlyType(type);

  /**
   * The two halves as one list.
   *
   * The halves must be INTERLEAVED. Appending one to the other puts every billing row after
   * every connector row, which reads as "billing ran last" no matter when anything ran.
   * `tiebreak` is newest-first then job id — the same fallback order every sorted column here
   * uses, so this list and a sorted one agree on ties.
   *
   * The order set here is what the table shows only if someone clears the sort by clicking
   * their way back to no column; the default sort below re-orders it newest-first either way.
   * It is still worth getting right: `sorted` returns this array untouched in that case.
   */
  const jobs = useMemo(() => {
    const base = connectorExcluded ? EMPTY_JOBS : connectorJobs;
    if (billingVisible.length === 0) return base;
    return [...base, ...billingVisible].sort(tiebreak);
  }, [connectorJobs, billingVisible, connectorExcluded]);

  /**
   * The summary line counts BOTH halves.
   *
   * `stateCounts` describes the connector queue alone, so a brand tab could show three billing
   * failures under the words "Nothing in the queue" — and that line is what an admin reads
   * before the rows.
   *
   * Deliberately not merged into `stateCounts` itself: that value is also the "listed" side of
   * the disclosure below, which compares it against the connector's own totals to say how much
   * of the window went unlisted. Inflating it with billing rows would make that disclosure
   * quietly stop appearing.
   */
  const summaryCounts = useMemo(() => {
    if (billingRuns.length === 0) return stateCounts;
    const merged = { ...stateCounts };
    for (const job of billingRuns) merged[job.state] = (merged[job.state] || 0) + 1;
    return merged;
  }, [stateCounts, billingRuns]);

  // Built from the rows that actually came back, so the Job filter offers a billing type the
  // day it first runs instead of the day someone remembers to list it. Without it, billing's
  // rows were the only ones in this table no Job filter could select.
  const billingTypeOptions = useMemo(() => {
    const seen = new Map();
    for (const job of billingRuns) {
      if (job.type && !seen.has(job.type)) seen.set(job.type, job.label || job.type);
    }
    return [...seen.entries()].map(([value, label]) => ({ label, value }));
  }, [billingRuns]);

  /**
   * Each `sortKey` reads the SAME value its cell renders, not a neighbouring field. Sorting
   * State on `job.state` would ignore `skipPermanent` and scatter the Blocked rows through the
   * queue states they are drawn from; sorting Job on `job.type` would order by the internal
   * string ("account-order-sync") while the column reads "Fetch orders". A sort that disagrees
   * with the column it is under is worse than no sort.
   *
   * Reason is deliberately NOT sortable: it is a truncated free-text failure message, and
   * alphabetising stack traces answers nothing.
   */
  const columns = useMemo(() => [
    // Source leads, on BOTH placements. It is the column that says which of two systems a row
    // came from, and several cells below mean something slightly different depending on the
    // answer — a retry limit set by whichever side queued it, a retailer a billing job was
    // never scoped to.
    //
    // It used to be brand-only, on the grounds that every value would be the same word on the
    // global page. That was true while the global page read the connector route alone; it now
    // reads the merged one, so both systems appear on both screens and the column has two
    // values wherever it is drawn.
    {
      key: 'source',
      title: 'Source',
      tip: SOURCE_COLUMN_TIP,
      firstClick: 'ascending',
      sortKey: (job) => sourceLabel(job.source),
    },
    { key: 'state', title: 'State',
      tip: 'Where the job is right now. Queued includes work held with a priority, which the queue keeps in a separate set — it is waiting, not stuck.',
      // Worst first on the first click. The default would be descending, which opens this
      // column on "Done".
      firstClick: 'ascending',
      sortKey: (job) => {
        const rank = STATE_RANK.indexOf(job.skipPermanent ? 'blocked' : job.state);
        // An unrecognised state renders as the raw string in a neutral badge, and there is no
        // honest place for it in a severity order — so it goes to the end, like any blank.
        return rank === -1 ? null : rank;
      } },
    {
      key: 'job',
      title: 'Job',
      tip: 'What the job does. Manual means someone pressed a button rather than a schedule '
        + 'firing it. A manual READ runs even with its own toggle off — that is the point of '
        + 'being able to look at a retailer without touching it. A manual WRITE does not: '
        + 'those refuse while the operation is switched off, which is why their buttons are '
        + 'disabled.',
      firstClick: 'ascending',
      sortKey: (job) => JOB_LABELS[job.type] || job.type || null,
    },
    { key: 'who', title: 'Retailer / Brand',
      tip: 'Which retailer account, and which brand, the job is for. A connector job that fans out across retailers shows the brand only. A billing job is not scoped to a retailer at all and says so, because the same blank would otherwise read as a fan-out.',
      firstClick: 'ascending',
      // Retailer leads, brand breaks the tie — the reading order of the two lines in the cell.
      // A job that names neither renders "All retailers"; returning null puts every one of
      // those together at the end instead of interleaving them under an invented name.
      sortKey: (job) => [retailerOf(job), brandOf(job)].filter(Boolean).join(' · ') || null },
    {
      key: 'attempts',
      title: 'Attempts',
      tip: 'How many times this job has run against the limit it was queued with. The limit '
        + 'differs by job, so "2 of 2" and "2 of 3" mean different things.',
      firstClick: 'descending',
      // The NUMBER, never the "1 of 2" string the cell prints: lexically, "10 of 10" sorts
      // between "1 of 2" and "2 of 3", so the row that has retried most would read as second.
      // The condition is the cell's own — anything it shows as "—" sorts as blank.
      sortKey: (job) => {
        if (!(job.attemptsMade > 0 || job.state === 'failed')) return null;
        const n = Number(job.attemptsMade);
        return Number.isFinite(n) ? n : null;
      },
    },
    {
      key: 'reason',
      title: 'Reason',
      tip: 'Why a job failed, or why it did nothing. Skipped is not a failure — it is usually '
        + 'an operation switched off in Sync Settings, which is worth seeing because a '
        + 'switched-off connector otherwise looks like a quiet one.',
    },
    { key: 'when', title: 'Date & Time',
      tip: 'When the job reached this state — finished for a failure, started for active work, queued for anything waiting. Hover the value for how long ago.',
      firstClick: 'descending',
      sortKey: (job) => {
        // Same guard as the cell: it prints "—" for a missing or unparseable stamp.
        if (!job.when) return null;
        const t = new Date(job.when).getTime();
        return Number.isNaN(t) ? null : t;
      } },
    // The eye. Clicking the row already opened the detail, but nothing on screen said so —
    // an affordance a person has to guess at is one most never find. Not sortable: it is a
    // control, and a sort arrow on it would offer an ordering that does not exist.
    { key: 'action', title: 'Action',
      tip: 'Open the full record of this job: what it was doing, which retailer or brand it was for, how many times it has been tried, and the whole reason if it stopped.' },
    // Both placements draw the same columns now that Source is unconditional, so nothing here
    // depends on the scope. Kept as a memo because the identity is read by the sort lookup and
    // by sortToggleLabels below, which would otherwise rebuild on every poll.
  ], []);

  // -1 when the stored column is not on offer in this placement — which cannot happen for the
  // default, but would if a future column were dropped on one of the two pages while sorted.
  // Treated as "not sorted" rather than indexing `columns[-1]`.
  const sortIndex = useMemo(() => {
    const i = columns.findIndex((c) => c.key === sortColumnKey);
    return i === -1 ? null : i;
  }, [columns, sortColumnKey]);

  const sortColumn = sortIndex === null ? null : columns[sortIndex];

  /**
   * Sorted across the WHOLE window, before the page is sliced off it.
   *
   * Sorting `visible` would reorder the twenty rows already on screen while the heading claimed
   * to have sorted the queue — so "worst first" would surface the worst row ON THIS PAGE and
   * quietly leave a failure sitting on page 4. The array being sorted is the same one the pager
   * measures itself against.
   *
   * Memoised so the 15-second poll and every unrelated re-render (opening the modal, touching a
   * Select, turning a page) do not re-run the comparator over the full window.
   */
  const sorted = useMemo(() => {
    if (!sortColumn?.sortKey) return jobs;
    const { sortKey } = sortColumn;
    const factor = sortDirection === 'descending' ? -1 : 1;
    // A copy: `jobs` is the array held in state, and sorting in place would mutate it.
    return [...jobs].sort((a, b) => {
      const ka = sortKey(a);
      const kb = sortKey(b);
      const aBlank = isBlank(ka);
      const bBlank = isBlank(kb);
      // Blanks last in BOTH directions, so `factor` is deliberately not applied here.
      if (aBlank || bBlank) {
        if (aBlank && bBlank) return tiebreak(a, b);
        return aBlank ? 1 : -1;
      }
      return factor * compareValues(ka, kb) || tiebreak(a, b);
    });
  }, [jobs, sortColumn, sortDirection]);

  // Paged HERE, not by the API: getQueueJobs returns the whole window it keeps (48 hours or
  // 500 jobs for failures, 2 hours or 100 for completions) in one array, and this table used
  // to render every one of it. Slicing the array we already hold adds no request and leaves
  // the poll untouched.
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));

  // The page is CLAMPED rather than trusted. This table re-reads itself every 15 seconds and
  // the queue drains while someone is looking at it, so the array can shrink under a page that
  // was valid when it was chosen — and an out-of-range slice is empty, which would render the
  // "nothing queued" row on a queue that still has jobs in it.
  const safePage = Math.min(page, totalPages);
  const visible = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  const retailerSelectOptions = [{ label: 'All retailers', value: '' }, ...marketplaceOptions];
  const brandSelectOptions = [{ label: 'All brands', value: '' }, ...brandOptions];

  const headings = columns.map((c) => ({
    id: c.key,
    title: c.title,
    ...(c.tip ? { tooltipContent: c.tip, tooltipWidth: 'wide' } : {}),
    ...(c.firstClick ? { defaultSortDirection: c.firstClick } : {}),
  }));

  const sortable = columns.map((c) => Boolean(c.sortKey));

  /**
   * Expanded to EVERY index, sortable or not. Polaris indexes this map with no guard, so the
   * total map is the whole defence against the TypeError — and it cannot go stale when a
   * column's sortability changes, because it is built from the column list itself.
   */
  const sortToggleLabels = useMemo(
    () => Object.fromEntries(
      columns.map((c, i) => [i, SORT_TOGGLE_LABELS[c.key] || SORT_TOGGLE_FALLBACK]),
    ),
    [columns],
  );

  const handleSort = useCallback((index, direction) => {
    setSortColumnKey(columns[index]?.key ?? null);
    setSortDirection(direction);
  }, [columns]);


  return (
    <BlockStack gap="300">
      {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}

      {/* Warning, not critical, and separate from the one above: the table is still showing
          real connector rows. One banner covering both halves would either overstate a
          billing outage or hide it behind rows that loaded fine. */}
      {billingError && (
        <Banner tone="warning" onDismiss={() => setBillingError(null)}>{billingError}</Banner>
      )}

      <InlineStack gap="200" wrap blockAlign="center">
        <Box width="160px">
          <Select label="State" labelHidden options={stateOptions()} value={state} onChange={setState} />
        </Box>
        <Box width="200px">
          <Select label="Job" labelHidden options={typeOptions(brandScoped, billingTypeOptions)} value={type} onChange={setType} />
        </Box>
        {marketplaceOptions.length > 0 && (
          <Box width="180px">
            <Select
              label="Retailer"
              labelHidden
              options={retailerSelectOptions}
              value={marketplaceId}
              onChange={setMarketplaceId}
            />
          </Box>
        )}
        {/* Unscoped only — on a brand's tab every row is already that brand's. */}
        {!brandScoped && brandOptions.length > 0 && (
          <Box width="180px">
            <Select
              label="Brand"
              labelHidden
              options={brandSelectOptions}
              value={brandFilter}
              onChange={setBrandFilter}
            />
          </Box>
        )}
        {filtered && (
          <Button
            variant="plain"
            onClick={() => { setState(''); setType(''); setMarketplaceId(''); setBrandFilter(''); }}
          >
            Clear
          </Button>
        )}
        <Button size="slim" onClick={() => load()} loading={loading}>Refresh</Button>
      </InlineStack>

      {/* The counts strip that stood here is gone. It restated what the State column and
          the filters already show, in a paragraph that grew every time a case needed
          explaining — the unlisted-rows caveat, then the retailer-accounts note, then a line
          about billing. Four lines of prose above five rows is read once and skipped after
          that. The Source column and the State filter carry the same facts where a reader is
          already looking. */}

      {/* Headings stay visible when there are no rows: IndexTable's emptyState replaces the
          whole table at itemCount 0, headings included, and a table with no column names
          reads as broken rather than empty. Which matters more now the headings are the sort
          control — an empty page would take the sort away with them. */}
      <IndexTable
        resourceName={{ singular: 'job', plural: 'jobs' }}
        itemCount={visible.length || 1}
        headings={headings}
        sortable={sortable}
        sortToggleLabels={sortToggleLabels}
        sortColumnIndex={sortIndex ?? undefined}
        sortDirection={sortDirection}
        onSort={handleSort}
        loading={loading}
        selectable={false}
      >
        {visible.length === 0 && (
          <IndexTable.Row id="no-jobs" position={0}>
            <IndexTable.Cell colSpan={columns.length}>
              <Box padding="500">
                <Text alignment="center" tone="subdued">
                  {/* Not "everything is quiet" when the request failed — see SkuMapTable
                      for the same fix. And the window is stated per state: completions are
                      kept 2 hours or 100 jobs, failures 48 hours or 500, so an empty table
                      is a stronger statement than the old copy implied. */}
                  {error && !data
                    ? 'Could not read the queue — see the error above.'
                    : filtered
                      ? 'No jobs match these filters.'
                      : 'Nothing queued, running or scheduled, no failure in the last 48 hours, and no completed job in the last two hours — which is normal on a quiet connector.'}
                </Text>
              </Box>
            </IndexTable.Cell>
          </IndexTable.Row>
        )}
        {visible.map((job, i) => {
          // A permanent skip is not the same event as a toggle skip, and they used to look
          // identical. A deactivated account, a missing API key or a stale scheduler with no
          // job type will re-fire every interval forever and needs a person; a toggle skip
          // resolves itself the moment someone flips the switch back.
          const badge = job.skipPermanent
            ? { tone: 'critical', label: 'Blocked' }
            : (STATE_BADGE[job.state] || { tone: 'new', label: job.state });
          // A run's own note is shown as its Reason when there is no error. A row that
          // recorded why it did nothing is not the same row as one that recorded nothing.
          const reason = job.failedReason || job.skipReason || job.note || null;
          return (
            <IndexTable.Row
              id={`${job.jobId}:${job.state}`}
              key={`${job.jobId}:${job.state}`}
              position={i}
              onClick={() => setOpenJob(job)}
            >
              {/* UNCONDITIONAL, exactly like its heading. A cell gated on something the
                  heading is not shifts every column after it along by one — Source shows the
                  state, State shows the job — and it renders perfectly, so nothing but reading
                  a row says it happened. */}
              <IndexTable.Cell>
                {/* No tone: Polaris puts a visually-hidden tone word inside a toned badge,
                    so it would read as "Info Billing". Source is an origin, not a state —
                    the badge next to it is the one carrying severity. */}
                <Tooltip content={sourceTip(job.source || 'connector')}>
                  <Badge>{sourceLabel(job.source)}</Badge>
                </Tooltip>
              </IndexTable.Cell>
              <IndexTable.Cell>
                {/* The hover is only on a billing row. A connector row's state is already
                    explained by its Reason cell and by the heading tip; a billing row's is
                    not, and its attempt limit and note live nowhere else on this line. */}
                {job.run ? (
                  <Tooltip content={statusTip(job.run)}>
                    <Badge tone={badge.tone}>{badge.label}</Badge>
                  </Tooltip>
                ) : (
                  <Badge tone={badge.tone}>{badge.label}</Badge>
                )}
              </IndexTable.Cell>
              <IndexTable.Cell>
                <BlockStack gap="050">
                  <Tooltip content={`Job type: ${job.type}`}>
                    <Text variant="bodyMd" fontWeight="semibold">
                      {job.label || JOB_LABELS[job.type] || job.type}
                    </Text>
                  </Tooltip>
                  {job.manual && (
                    <Text variant="bodySm" tone="subdued">Manual run</Text>
                  )}
                </BlockStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <BlockStack gap="050">
                  {/* A job outlives the row it names. Saying so beats printing an id that
                      reads as a name. Read through the same helpers the sort key uses, so a
                      billing row — which carries the names and no ids — is not blanked. */}
                  {retailerOf(job) && (
                    <Text variant="bodyMd">{retailerOf(job)}</Text>
                  )}
                  {/* Said explicitly, and above the brand, because on a CONNECTOR row a brand
                      with no retailer means the job fanned out across all of them — which is
                      the opposite of what it means on a billing row, where there is no
                      retailer to fan out to. */}
                  {!retailerOf(job) && job.source === 'billing' && (
                    <Text variant="bodySm" tone="subdued">No retailer</Text>
                  )}
                  {brandOf(job) && (
                    <Text variant="bodySm" tone="subdued">{brandOf(job)}</Text>
                  )}
                  {!retailerOf(job) && !brandOf(job) && job.source !== 'billing' && (
                    <Text variant="bodySm" tone="subdued">All retailers</Text>
                  )}
                </BlockStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                {job.attemptsMade > 0 || job.state === 'failed' ? (
                  <Text variant="bodySm" numeric>
                    {job.attemptsMade}{job.attempts ? ` of ${job.attempts}` : ''}
                  </Text>
                ) : (
                  <Text variant="bodySm" tone="subdued">—</Text>
                )}
              </IndexTable.Cell>
              <IndexTable.Cell>
                {reason ? (
                  <Tooltip content={reason}>
                    <Text variant="bodySm" tone={job.state === 'failed' ? 'critical' : 'subdued'}>
                      {truncate(reason)}
                    </Text>
                  </Tooltip>
                ) : (
                  <Text variant="bodySm" tone="subdued">—</Text>
                )}
              </IndexTable.Cell>
              <IndexTable.Cell>
                {job.when ? (
                  <Tooltip content={relative(job.when)}>
                    <Text variant="bodySm" tone="subdued">{when(job.when)}</Text>
                  </Tooltip>
                ) : (
                  <Text variant="bodySm" tone="subdued">—</Text>
                )}
              </IndexTable.Cell>
              {/* stopPropagation, because the row itself also opens this — without it the
                  click would open and immediately reopen, which React renders as a flicker. */}
              <IndexTable.Cell>
                <Tooltip content="See everything about this job">
                  <Button
                    size="slim"
                    icon={ViewIcon}
                    accessibilityLabel={`See everything about this ${job.label || job.type} job`}
                    onClick={(e) => { e?.stopPropagation?.(); setOpenJob(job); }}
                  />
                </Tooltip>
              </IndexTable.Cell>
            </IndexTable.Row>
          );
        })}
      </IndexTable>

      {/* Both controls in one row, and only once there are rows to page through — a pager
          under an empty table is three dead words. The pager itself is dropped when
          everything fits, but the row count stays: it is how someone GETS more than one
          page, so hiding it at size 50 would strand them there. */}
      {jobs.length > 0 && (
        <InlineStack align="space-between" blockAlign="center">
          <PageSizeSelect value={pageSize} onChange={setPageSize} />
          {totalPages > 1 && (
            /* Driven by `safePage`, the value the rows were actually sliced with. Labelling
               with the raw `page` would print "Page 6 of 3" for as long as a drained queue
               stayed drained. */
            <Pagination
              hasPrevious={safePage > 1}
              onPrevious={() => setPage(safePage - 1)}
              hasNext={safePage < totalPages}
              onNext={() => setPage(safePage + 1)}
              label={`Page ${safePage} of ${totalPages}`}
            />
          )}
        </InlineStack>
      )}

      {/* The full reason, verbatim. A failure reason can be a stack trace, so it wraps and
          scrolls rather than being truncated — this is the one place where the whole text is
          the point, and it is why the Reason column can afford to be short. */}
      <Modal
        open={Boolean(openJob)}
        onClose={() => setOpenJob(null)}
        title={openJob ? (openJob.label || JOB_LABELS[openJob.type] || openJob.type) : 'Job'}
        secondaryActions={[{ content: 'Close', onAction: () => setOpenJob(null) }]}
        large
      >
        {openJob && (
          <Modal.Section>
            <BlockStack gap="300">
              {/* The raw type and the queue's internal id are gone. Neither appears on any
                  other screen, so there was nothing to look either of them up against. */}
              <InlineStack gap="200" blockAlign="center" wrap>
                <Badge tone={(STATE_BADGE[openJob.state] || {}).tone || 'new'}>
                  {(STATE_BADGE[openJob.state] || {}).label || openJob.state}
                </Badge>
                {openJob.source === 'billing' && <Badge>Billing</Badge>}
                {openJob.manual && <Badge>Started by a person</Badge>}
              </InlineStack>

              {JOB_EXPLAIN[openJob.type] && (
                <Text variant="bodyMd">{JOB_EXPLAIN[openJob.type]}</Text>
              )}

              <Text variant="bodySm" tone="subdued">
                {NEXT_STEP[openJob.state] || 'Its state is not one this screen has wording for.'}
              </Text>

              <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
                <BlockStack gap="050">
                  <Text variant="bodySm" tone="subdued">Retailer</Text>
                  <Text variant="bodyMd">
                    {openJob.marketplaceName
                      || (openJob.source === 'billing'
                        ? 'None — billing does not talk to a retailer'
                        : 'Every retailer this covers')}
                  </Text>
                </BlockStack>
                <BlockStack gap="050">
                  <Text variant="bodySm" tone="subdued">Brand</Text>
                  {/* Most retailer work covers every brand on the account at once and is
                      recorded against none of them. Saying so beats a blank. */}
                  <Text variant="bodyMd">
                    {openJob.brandName || 'Every brand on this retailer account'}
                  </Text>
                </BlockStack>
                <BlockStack gap="050">
                  <Text variant="bodySm" tone="subdued">Started by</Text>
                  <Text variant="bodyMd">
                    {openJob.manual ? 'A person, from the console' : 'The schedule in Sync Settings'}
                  </Text>
                </BlockStack>
                <BlockStack gap="050">
                  <Text variant="bodySm" tone="subdued">
                    {openJob.state === 'delayed' ? 'Due to run' : 'Last change'}
                  </Text>
                  <Text variant="bodyMd">
                    {openJob.when ? new Date(openJob.when).toLocaleString() : 'Not recorded'}
                  </Text>
                </BlockStack>
                <BlockStack gap="050">
                  <Text variant="bodySm" tone="subdued">Queued</Text>
                  <Text variant="bodyMd">
                    {openJob.timestamp ? new Date(openJob.timestamp).toLocaleString() : 'Not recorded'}
                  </Text>
                </BlockStack>
                <BlockStack gap="050">
                  <Text variant="bodySm" tone="subdued">Attempts</Text>
                  {/* A missing count is stated, not printed as a blank after the word
                      "Attempt". Billing records one only once a run has been attempted. */}
                  <Text variant="bodyMd">
                    {openJob.attemptsMade === null || openJob.attemptsMade === undefined
                      ? 'Not recorded'
                      : `${openJob.attemptsMade} so far${openJob.attempts ? `, up to ${openJob.attempts} allowed` : ''}`}
                  </Text>
                </BlockStack>
              </InlineGrid>

              {(openJob.failedReason || openJob.skipReason || openJob.note) && (
                <Box background="bg-surface-secondary" padding="200" borderRadius="100" overflowX="scroll">
                  <Text variant="bodySm" breakWord as="pre">
                    {openJob.failedReason || openJob.skipReason || openJob.note}
                  </Text>
                </Box>
              )}

              {!openJob.failedReason && !openJob.skipReason && !openJob.note && (
                <Text variant="bodySm" tone="subdued">
                  Nothing has gone wrong with this one — a reason appears here only when a
                  job fails or is skipped.
                </Text>
              )}

              {/* A missing attempt count is stated, not printed as a blank after the word
                  "Attempt". Billing records one only once a run has actually been attempted. */}
            </BlockStack>
          </Modal.Section>
        )}
      </Modal>
    </BlockStack>
  );
}
